import { widget } from "@foretagsplatsen/widgetjs";

/**
 *
 *`categorizedListWidget` is used to display a map of items where keys are
 *categories and values are lists of items.
 *
 *Example of categories:
 *
 *{
 *Dog: ['Pluto', 'Charlie', 'Lassie'],
 *Cat: ['Garfield', 'Felix']
 *}
 *
 * @param {{}} spec - Parameter object
 * @param {{}} spec.categories                        - map containing the categories to display as well as the items.
 * @param {function} spec.itemRenderer                - function returning a renderable object for each item.
 * @param {function} spec.categoryRenderer            - function returning a renderable object for each category.
 * @param {function} spec.itemsSorter                 - function used to sort the item when rendered.
 * @param {string} [spec.selectedCategory]  - category to show. Show all categories by default
 * @param {*} my - protected fields holder
 * @return {*}
 */
function categorizedListWidget(spec, my) {
	spec ||= {};
	my ||= {};
	const that = widget(spec, my);

	my.categories = spec.categories;
	my.itemRenderer = spec.itemRenderer;
	my.categoryRenderer = spec.categoryRenderer;

	my.selectedCategory = spec.selectedCategory || undefined;
	my.filter = "";

	my.itemSearchString =
		spec.itemSearchString ||
		function (item) {
			return item.toString();
		};

	my.itemsSorter =
		spec.itemsSorter ||
		function () {
			return true;
		};

	my.selectedItems = spec.selectedItems || [];

	my.renderedItems = [];

	that.getRenderedItems = function () {
		return my.renderedItems;
	};

	that.setCategories = function (newCategories) {
		my.categories = newCategories;
		that.update();
	};

	that.showCategory = function (category) {
		if (my.categories.indexOf(category) === -1) {
			return;
		}
		my.selectedCategory = category;
		that.update();
	};

	that.showAll = function () {
		my.selectedCategory = undefined;
		that.update();
	};

	that.resetSearch = function () {
		my.filter = "";
		that.update();
	};

	that.applySearchFrom = function (searchField) {
		searchField.applySearchOnList(that);
	};

	that.updateSearch = function (string) {
		my.selectedCategory = undefined;
		my.filter = string.toLowerCase();
		that.update();
	};

	that.renderContentOn = function (html) {
		my.renderedItems = [];
		if (my.selectedCategory) {
			my.renderCategoryOn(html);
		} else {
			my.renderAllOn(html);
		}
	};

	my.renderCategoryOn = function (html) {
		html.render(my.categoryRenderer(my.selectedCategory));

		my.selectedCategory
			.getItems()
			.sort(my.itemsSorter)
			.forEach((item) => {
				if (!my.itemFilter(item)) {
					return;
				}
				my.renderItemOn(html, item, my.selectedCategory);
			});
	};

	my.renderAllOn = function (html) {
		my.categoriesToShow().forEach((category) => {
			const items = category.getItems().filter(my.itemFilter);
			if (items.length) {
				html.render(my.categoryRenderer(category));

				items.sort(my.itemsSorter).forEach((item) => {
					my.renderItemOn(html, item, category);
				});
			}
		});
	};

	my.renderItemOn = function (html, item, category) {
		my.renderedItems.push(item);
		html.render(my.itemRenderer(item, category));
	};

	my.categoriesToShow = function () {
		return my.categories;
	};

	my.itemFilter = function (item) {
		const selected = itemSelected(item);
		if (my.filter) {
			return (
				!selected &&
				my.itemSearchString(item).toLowerCase().indexOf(my.filter) !==
					-1
			);
		}
		return !selected;
	};

	function itemSelected(item) {
		return my.selectedItems.filter((selectedItem) => {
			if (item.getBasId()) {
				return (
					selectedItem.getBasId() ===
					(item.getBasId() || item.getId())
				);
			}
			return selectedItem.getId() === item.getId();
		}).length;
	}

	return that;
}

export default categorizedListWidget;
